import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useFocusEffect, useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    FlatList,
    Platform,
    ScrollView,
    StyleSheet,
    Text as RNText,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { ComplianceBanner } from "@/components/compliance/ComplianceBanner";
import { MyRequestsPanel, requestVisitRoute } from "@/components/MyRequestsPanel";
import { ProfileDrawer } from "@/components/ProfileDrawer";
import {
    type AvailableVisitListItem,
    type VisitListItem,
    type VisitRequestListItem,
    fetchAvailableVisits,
    fetchVisitRequests,
    fetchVisits
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { logRocketRedactProps } from "@/lib/logrocket";
import {
    type MapRegion,
    type PinCluster,
    type Viewport,
    clusterModeForRegion,
    clusterPins,
    clusterSplitRegion,
    clusterZIndex,
    collidingClusters,
    openingRegion,
    pinZIndex,
    regionChangedEnough,
    selectedRegion,
    zoomForRegion
} from "@/lib/map-clusters";
import { ensureMapsLoaded } from "@/lib/maps-loader";
import { Marker, PROVIDER_GOOGLE, RNMapView, type RNMapViewInstance } from "@/lib/native-maps";
import { fetchOnboardingProgress } from "@/lib/onboarding-api";
import { useSkipRule } from "@/lib/recorder/useSkipRule";
import {
    colors,
    exploreRelationStyle,
    exploreUnseenDot,
    fonts,
    gradients,
    radius,
    shadows,
    spacing,
    typography,
    visitTypeColors
} from "@/lib/theme";
import { useCoalescedRefetch } from "@/lib/use-coalesced-refetch";
import { useDedupedState } from "@/lib/use-deduped-state";
import {
    EXACT_ADDRESS_ONCE_YOURS_TEXT,
    distanceAwayText,
    showsExactAddressHint,
    visitLocationLine,
    withholdsExactAddress
} from "@/lib/visit-location";
import { useWebSocketEvent } from "@/lib/websocket";

// ═══════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════

type AvailableVisit = AvailableVisitListItem;

/** One bubble on the map: either a lone visit or several whose bubbles would
 *  overlap at the current zoom. */
type VisitCluster = PinCluster<AvailableVisit>;

/** Pin/badge tokens for how this nurse stands with a visit. Falls back to the
 *  neutral `open` styling if the server ever sends a relation this build of the
 *  app doesn't know about yet (an OTA can lag a deploy). */
function relationStyle(visit: AvailableVisit) {
    return exploreRelationStyle[visit.relation] ?? exploreRelationStyle.open;
}

/** She has never opened this visit's detail screen — the pink dot on the pin and
 *  the card avatar. This is the ONLY thing on the Explore screen that means
 *  "new": the visit-age badge (`isNew`, posted < 48h ago) used to sit alongside
 *  it and was removed, because two marks both reading as "new" — one about the
 *  visit, one about her — is worse than losing the weaker of the two.
 *
 *  A visit that's already hers never counts as unseen. The dot means "you
 *  haven't looked at this offer yet", and an assigned visit isn't an offer —
 *  she requested it and won it. It also *couldn't* clear: assigned visits open
 *  /visit/:id, which records no view, so the dot would have stuck forever. */
function isUnseen(visit: AvailableVisit): boolean {
    return visit.relation !== "assigned" && visit.viewed == null;
}

/** Map visit type codes to display labels */
const VISIT_TYPE_LABELS: Record<string, string> = {
    SOC: "Start of Care",
    REA: "Reassessment",
    SUP: "Supervisory",
    DC: "Discharge",
    ROC: "Resumption of Care"
};

// ═══════════════════════════════════════════════════
// Logo Component
// ═══════════════════════════════════════════════════

function PinwheelLogo({ size = 34 }: { size?: number }) {
    const half = size / 2;
    return (
        <View style={{ width: size, height: size }}>
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.teal400,
                    borderTopLeftRadius: half,
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: half,
                    borderBottomLeftRadius: 0
                }}
            />
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.pink400,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: half,
                    borderBottomRightRadius: 0,
                    borderBottomLeftRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.pink400,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: half,
                    borderBottomRightRadius: 0,
                    borderBottomLeftRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.teal400,
                    borderTopLeftRadius: half,
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: half,
                    borderBottomLeftRadius: 0
                }}
            />
        </View>
    );
}

// ═══════════════════════════════════════════════════
// Map Pin — Navy bubble with $amount + colored dot
// ═══════════════════════════════════════════════════

// ═══════════════════════════════════════════════════
// Map Pin Popup Card (bottom sheet style)
// ═══════════════════════════════════════════════════

function MapPinCard({
    visit,
    onViewMore,
    onClose
}: {
    visit: AvailableVisit;
    onViewMore: () => void;
    onClose: () => void;
}) {
    const vtColor = visitTypeColors[visit.visitType] ?? visitTypeColors.SOC;
    const relStyle = relationStyle(visit);

    return (
        <View style={styles.pinCardWrapper}>
            <View style={styles.pinCard}>
                <LinearGradient
                    colors={[...gradients.brand]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={styles.cardTopAccent}
                />
                <View style={styles.pinCardContent}>
                    {/* Close handle */}
                    <TouchableOpacity
                        onPress={onClose}
                        style={styles.pinCardCloseHandle}
                        hitSlop={12}
                    >
                        <View style={styles.pinCardHandle} />
                    </TouchableOpacity>

                    {/* Top row: avatar + info + payment */}
                    <View style={styles.pinCardTopRow}>
                        <View style={[styles.pinCardAvatar, { backgroundColor: vtColor.text }]}>
                            <Text style={styles.pinCardAvatarText}>{visit.patientInitials}</Text>
                        </View>

                        <View style={styles.pinCardInfo}>
                            {/* Payment on same line */}
                            <View style={styles.pinCardHeadline}>
                                <Text style={styles.pinCardDateRange}>
                                    {visit.distanceMiles} mi away
                                </Text>
                                <View style={styles.pinCardPayment}>
                                    <Text style={styles.pinCardPaymentAmount}>
                                        ${visit.payment}
                                    </Text>
                                    <View style={styles.pinCardDollarIcon}>
                                        <MaterialCommunityIcons
                                            name="currency-usd"
                                            size={14}
                                            color={colors.teal500}
                                        />
                                    </View>
                                </View>
                            </View>
                            {/* Anonymized (initials only) while broadcasted;
                                the full name appears once the RN has requested
                                this visit (`patientName` is set server-side). */}
                            <Text style={styles.pinCardPatientName} numberOfLines={1}>
                                {visit.patientName ?? `Patient ${visit.patientInitials}`}
                            </Text>
                            {/* Cross street (or the area) until the visit is
                                assigned to her (PR-18, revised 2026-10-06): the
                                street address arrives then, not on request. */}
                            <Text style={styles.pinCardAddress} numberOfLines={2}>
                                {visitLocationLine(visit)}
                            </Text>
                            {showsExactAddressHint(visit) ? (
                                <Text style={styles.pinCardAreaHint} numberOfLines={1}>
                                    {EXACT_ADDRESS_ONCE_YOURS_TEXT}
                                </Text>
                            ) : null}
                        </View>
                    </View>

                    {/* Visit type + duration + status. The relation chip restates
                        the pin colour in words — the pin says amber, this says
                        "Requested" — so the colour never has to be learned from
                        memory.

                        The old visit-age "New" mark (visit posted < 48h ago) is
                        deliberately gone from this screen: it competed with the
                        pink "never opened" dot, and two things labelled "new"
                        meaning different things on one card is worse than losing
                        the weaker signal. `isNew` still exists on the payload
                        and still drives the Smart Picks screen. */}
                    <View style={styles.pinCardTypeRow}>
                        <View style={styles.pinCardTypeLeft}>
                            <Text style={styles.pinCardTypeText}>
                                {visit.visitTypeDisplayName ??
                                    VISIT_TYPE_LABELS[visit.visitType] ??
                                    visit.visitType}
                            </Text>
                            <Text style={styles.pinCardTypeSeparator}>/</Text>
                            <Text style={styles.pinCardDuration}>
                                {visit.durationMinutes} minutes
                            </Text>
                        </View>
                        {relStyle.label ? (
                            <View
                                style={[
                                    styles.pinCardRelationChip,
                                    { backgroundColor: relStyle.badgeBg }
                                ]}
                            >
                                <Text
                                    style={[styles.pinCardRelationText, { color: relStyle.badge }]}
                                >
                                    {relStyle.label}
                                </Text>
                            </View>
                        ) : null}
                    </View>

                    {/* Distance row */}
                    <View style={styles.travelRow}>
                        <View style={styles.travelItem}>
                            <MaterialCommunityIcons
                                name="map-marker-distance"
                                size={16}
                                color={colors.textSecondary}
                            />
                            <Text style={styles.travelText}>{distanceAwayText(visit)}</Text>
                        </View>
                        <View style={styles.travelDot} />
                        <View style={styles.travelItem}>
                            <MaterialCommunityIcons
                                name="timer-outline"
                                size={16}
                                color={colors.textSecondary}
                            />
                            <Text style={styles.travelText}>{visit.durationMinutes} min</Text>
                        </View>
                    </View>

                    {/* CTA Button */}
                    <TouchableOpacity activeOpacity={0.8} onPress={onViewMore}>
                        <LinearGradient
                            colors={[...gradients.brand]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={styles.ctaButton}
                        >
                            <Text style={styles.ctaText}>
                                {visit.relation === "assigned"
                                    ? "Open my visit"
                                    : "Task information"}
                            </Text>
                            <View style={styles.ctaArrowCircle}>
                                <MaterialCommunityIcons
                                    name="arrow-right"
                                    size={16}
                                    color={colors.teal400}
                                />
                            </View>
                        </LinearGradient>
                    </TouchableOpacity>
                </View>
            </View>
        </View>
    );
}

// ═══════════════════════════════════════════════════
// Visit Card (for cards list view)
// ═══════════════════════════════════════════════════

function VisitCard({ visit, onPress }: { visit: AvailableVisit; onPress: () => void }) {
    const vtColor = visitTypeColors[visit.visitType] ?? visitTypeColors.SOC;
    const relStyle = relationStyle(visit);

    return (
        <TouchableOpacity activeOpacity={0.7} onPress={onPress} style={styles.visitCard}>
            <LinearGradient
                colors={[...gradients.brand]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.cardTopAccent}
            />
            <View style={styles.cardContent}>
                {/* Avatar, with the "never opened" dot on its corner — same
                    signal as the map pin, same place relative to the pay. */}
                <View style={[styles.cardAvatar, { backgroundColor: vtColor.text }]}>
                    <Text style={styles.cardAvatarText}>{visit.patientInitials}</Text>
                    {isUnseen(visit) && <View style={styles.cardUnseenDot} />}
                </View>

                {/* Middle + Right together */}
                <View style={styles.cardBody}>
                    {/* Patient name — only revealed once this RN has requested
                        the visit; broadcasted cards stay anonymized (initials
                        in the avatar only). */}
                    {visit.patientName ? (
                        <Text style={styles.cardPatientName} numberOfLines={1}>
                            {visit.patientName}
                        </Text>
                    ) : null}

                    {/* Row 1: Date range + Payment */}
                    <View style={styles.cardTopLine}>
                        <Text style={styles.cardDateRange}>{visit.distanceMiles} mi away</Text>
                        <Text style={styles.cardPayment}>${visit.payment}</Text>
                    </View>

                    {/* Row 2: Chips */}
                    <View style={styles.chipRow}>
                        <View style={[styles.chip, { backgroundColor: vtColor.bg }]}>
                            <Text style={[styles.chipText, { color: vtColor.text }]}>
                                {visit.visitTypeDisplayName ??
                                    VISIT_TYPE_LABELS[visit.visitType] ??
                                    visit.visitType}
                            </Text>
                        </View>
                        <View style={[styles.chip, { backgroundColor: colors.gray100 }]}>
                            <MaterialCommunityIcons
                                name="map-marker-outline"
                                size={10}
                                color={colors.textSecondary}
                            />
                            <Text style={[styles.chipText, { color: colors.textSecondary }]}>
                                {visit.distanceMiles} mi
                            </Text>
                        </View>
                        <View style={[styles.chip, { backgroundColor: colors.gray100 }]}>
                            <MaterialCommunityIcons
                                name="timer-outline"
                                size={10}
                                color={colors.textSecondary}
                            />
                            <Text style={[styles.chipText, { color: colors.textSecondary }]}>
                                {visit.durationMinutes} min
                            </Text>
                        </View>
                    </View>

                    {/* Row 3: Address + relation badge (Requested / Rejected /
                        Cancelled / Yours), mirroring the map pin colour and the
                        My Requests panel. An untouched visit gets no badge —
                        "new to her" is the dot on the avatar, and the old
                        visit-age "New" badge was removed so only one thing on
                        this card means "new". */}
                    <View style={styles.cardBottomLine}>
                        <Text style={styles.cardAddress} numberOfLines={1}>
                            {visitLocationLine(visit)}
                        </Text>
                        {relStyle.label ? (
                            <View style={[styles.relationBadge, { backgroundColor: relStyle.pin }]}>
                                <Text style={styles.relationBadgeText}>{relStyle.label}</Text>
                            </View>
                        ) : null}
                    </View>
                </View>
            </View>
        </TouchableOpacity>
    );
}

// ═══════════════════════════════════════════════════
// Map View — Full screen with navy price bubbles
// ═══════════════════════════════════════════════════

// Light map style for Google Maps JS SDK
const MAP_STYLES = [
    { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
    { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#bdbdbd" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#ececec" }] },
    { featureType: "transit", stylers: [{ visibility: "off" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#e8f0f8" }] }
];

/**
 * The region the map was left at, kept outside the component on purpose.
 *
 * `<MapView>` unmounts every time she flips to the cards list, leaves the tab,
 * or backgrounds the app, and `initialRegion` only applies at mount — so every
 * return used to throw away her zoom and re-fit the whole feed. Module scope
 * (rather than a ref) is what survives that unmount. It resets on app restart,
 * which is the right lifetime: a stale region from yesterday is not worth
 * restoring, but the one from thirty seconds ago is.
 */
let lastViewedRegion: MapRegion | null = null;

/**
 * What a bubble is allowed to merge with in `grouped` mode: its own colour.
 *
 * The colour is the nurse's relationship to the visit — green "yours", amber
 * "requested", navy an open offer — so letting a bubble hold a mix would make
 * the one thing the map says about a pin untrue. Different colours on the same
 * doorstep therefore stay separate bubbles, and the tap handler resolves that
 * overlap instead (see `handleClusterPress`).
 */
function pinColorKey(visit: AvailableVisit): string {
    return relationStyle(visit).pin;
}

/** The member a merged bubble takes its colour and dot from: the most urgent
 *  thing inside it, so a cluster holding one untouched offer reads as an offer
 *  and not as "visits you already own". Ties break on the better-paying visit,
 *  which is the one she'd want named first anyway. */
function clusterLead(members: AvailableVisit[]): AvailableVisit {
    return members.reduce((best, member) =>
        pinZIndex(member, false) > pinZIndex(best, false) ||
        (pinZIndex(member, false) === pinZIndex(best, false) && member.payment > best.payment)
            ? member
            : best
    );
}

/** `$90` for one visit, `$75–90` for a spread, `$90` again when a whole cluster
 *  happens to pay the same. */
function clusterPriceLabel(members: AvailableVisit[]): string {
    const low = Math.min(...members.map((m) => m.payment));
    const high = Math.max(...members.map((m) => m.payment));
    return low === high ? `$${low}` : `$${low}–${high}`;
}

function clusterHasUnseen(members: AvailableVisit[]): boolean {
    return members.some(isUnseen);
}

/** Overview circles grow with what's inside them, so a borough's worth of work
 *  reads differently from a couple of visits without needing a legend. */
function countCircleSize(count: number): number {
    if (count >= 25) {
        return 64;
    }
    if (count >= 10) {
        return 56;
    }
    return 46;
}

/**
 * The overview marker: a plain circle with a number in it.
 *
 * At this scale a bubble cannot honestly carry a colour (it holds visits of
 * several kinds) or a price (it holds a range that spans the whole feed), and
 * showing them anyway is what produced the unreadable pile. So it shows the one
 * fact that survives being zoomed out: how many visits are here. Tapping it
 * flies in, and the colours come back on the way down.
 */
function NativeCountCircle({
    cluster,
    isSelected
}: {
    cluster: VisitCluster;
    isSelected: boolean;
}) {
    const size = countCircleSize(cluster.members.length);

    return (
        <View style={styles.nativeCircleWrap} collapsable={false}>
            <View
                collapsable={false}
                style={[
                    styles.nativeCountCircle,
                    { width: size, height: size, borderRadius: size / 2 },
                    isSelected && styles.nativeCountCircleSelected
                ]}
            >
                <RNText style={[styles.nativeCountCircleText, { fontSize: size >= 56 ? 20 : 17 }]}>
                    {cluster.members.length}
                </RNText>
            </View>
        </View>
    );
}

/**
 * The pin bubble for both native platforms. iOS renders it inside a
 * react-native-maps `<Marker>`; Android positions it absolutely over the map.
 * They previously carried two hand-copied JSX trees, which is how the amber
 * "already requested" pin ended up shipping on web only — keep this the single
 * source for both.
 *
 * A bubble stands for a *cluster* rather than a visit: visits of the same
 * colour whose bubbles would overlap at this zoom are drawn once, with a count.
 * Merging is not cosmetic — visits for the same patient share
 * `patients.lat/lng`, so their pins are exactly coincident and the ones
 * underneath could never be tapped at any zoom.
 */
function NativeMarkerBubble({
    cluster,
    isSelected,
    platformStyle
}: {
    cluster: VisitCluster;
    isSelected: boolean;
    platformStyle: object;
}) {
    const lead = clusterLead(cluster.members);
    const count = cluster.members.length;
    const vtColor = visitTypeColors[lead.visitType]?.text ?? colors.teal400;
    const pin = relationStyle(lead).pin;

    return (
        <View style={styles.nativeMarkerWrap} collapsable={false}>
            <View
                collapsable={false}
                style={[
                    styles.nativeMarkerBubble,
                    platformStyle,
                    { backgroundColor: pin },
                    isSelected && styles.nativeMarkerBubbleSelected
                ]}
            >
                {count > 1 ? (
                    <View collapsable={false} style={styles.nativeMarkerCount}>
                        <RNText style={styles.nativeMarkerCountText}>{count}</RNText>
                    </View>
                ) : (
                    <View
                        collapsable={false}
                        style={[styles.nativeMarkerDot, { backgroundColor: vtColor }]}
                    />
                )}
                <RNText style={styles.nativeMarkerText}>
                    {clusterPriceLabel(cluster.members)}
                </RNText>
                {clusterHasUnseen(cluster.members) && (
                    <View collapsable={false} style={styles.nativeMarkerUnseen} />
                )}
            </View>
            <View collapsable={false} style={[styles.nativeMarkerArrow, { borderTopColor: pin }]} />
        </View>
    );
}

/**
 * The last resort for visits zoom cannot separate.
 *
 * Tapping a crowded bubble normally flies in and splits it, one level at a
 * time, until every visit stands alone. This sheet is only for the residue:
 * visits at the same address (they share `patients.lat/lng`, so they are
 * pixel-identical at any zoom) or on the same doorstep. Those can never be
 * teased apart on a map, so the map stops pretending and hands her a list.
 */
function MapClusterSheet({
    cluster,
    onSelect,
    onClose
}: {
    cluster: VisitCluster;
    onSelect: (visit: AvailableVisit) => void;
    onClose: () => void;
}) {
    // Most urgent first, so the untouched offers she is racing for are the ones
    // under her thumb rather than the visits she already owns.
    const ordered = useMemo(
        () =>
            [...cluster.members].sort(
                (a, b) => pinZIndex(b, false) - pinZIndex(a, false) || b.payment - a.payment
            ),
        [cluster.members]
    );
    // Offered visits arrive pinned at their cross street's intersection, or
    // rounded to 3 decimals (PR-18), so two patients near the same corner, or
    // in the same ~111 x 84 m square, land on exactly the same spot. When it
    // happens the pins are inseparable like a shared address and this sheet is
    // the only way in. It is "this area", not "this address".
    const sameArea = ordered.some(withholdsExactAddress);

    return (
        <View style={styles.clusterSheetWrapper}>
            <View style={styles.clusterSheet}>
                <LinearGradient
                    colors={[...gradients.brand]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={styles.cardTopAccent}
                />
                <TouchableOpacity onPress={onClose} style={styles.pinCardCloseHandle} hitSlop={12}>
                    <View style={styles.pinCardHandle} />
                </TouchableOpacity>

                <View style={styles.clusterSheetHeader}>
                    <Text style={styles.clusterSheetTitle}>
                        {ordered.length} visits {sameArea ? "in this area" : "at this address"}
                    </Text>
                    <Text style={styles.clusterSheetSubtitle}>
                        {sameArea
                            ? "Exact addresses appear once a visit is yours. Tap one to open it."
                            : "Too close to separate on the map — tap one to open it"}
                    </Text>
                </View>

                <ScrollView
                    style={styles.clusterSheetList}
                    showsVerticalScrollIndicator={false}
                    bounces={false}
                >
                    {ordered.map((visit) => {
                        const vtColor = visitTypeColors[visit.visitType] ?? visitTypeColors.SOC;
                        const relStyle = relationStyle(visit);

                        return (
                            <TouchableOpacity
                                key={visit.id}
                                activeOpacity={0.7}
                                onPress={() => onSelect(visit)}
                                style={styles.clusterRow}
                            >
                                <View
                                    style={[
                                        styles.clusterRowAvatar,
                                        { backgroundColor: vtColor.text }
                                    ]}
                                >
                                    <Text style={styles.clusterRowAvatarText}>
                                        {visit.patientInitials}
                                    </Text>
                                    {isUnseen(visit) && <View style={styles.cardUnseenDot} />}
                                </View>

                                <View style={styles.clusterRowBody}>
                                    <Text style={styles.clusterRowTitle} numberOfLines={1}>
                                        {visit.visitTypeDisplayName ??
                                            VISIT_TYPE_LABELS[visit.visitType] ??
                                            visit.visitType}
                                    </Text>
                                    <Text style={styles.clusterRowMeta} numberOfLines={1}>
                                        {visit.distanceMiles} mi · {visit.durationMinutes} min
                                    </Text>
                                </View>

                                <View style={styles.clusterRowRight}>
                                    <Text style={styles.clusterRowPayment}>${visit.payment}</Text>
                                    {relStyle.label ? (
                                        <View
                                            style={[
                                                styles.clusterRowBadge,
                                                { backgroundColor: relStyle.badgeBg }
                                            ]}
                                        >
                                            <Text
                                                style={[
                                                    styles.clusterRowBadgeText,
                                                    { color: relStyle.badge }
                                                ]}
                                            >
                                                {relStyle.label}
                                            </Text>
                                        </View>
                                    ) : null}
                                </View>
                            </TouchableOpacity>
                        );
                    })}
                </ScrollView>
            </View>
        </View>
    );
}

function MapView({
    visits,
    selectedPin,
    onPinPress,
    onPinClose,
    onViewMore
}: {
    visits: AvailableVisit[];
    selectedPin: AvailableVisit | null;
    onPinPress: (v: AvailableVisit) => void;
    onPinClose: () => void;
    onViewMore: (v: AvailableVisit) => void;
}) {
    const mapRef = useRef<View>(null);
    const mapInstanceRef = useRef<unknown>(null);

    // Visit Recording — pause capture while a map is rendered (off-screen
    // tile rendering doesn't snapshot reliably on Android).
    useSkipRule("map_open");
    const markersRef = useRef<unknown[]>([]);
    const nativeMapRef = useRef<RNMapViewInstance | null>(null);
    const androidOverlayRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const MAPS_KEY = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY ?? "";
    const [androidMarkerPoints, setAndroidMarkerPoints] = useState<
        Record<string, { x: number; y: number }>
    >({});
    const NativeMapView = Platform.OS === "web" ? null : RNMapView;
    const provider = Platform.OS === "android" ? PROVIDER_GOOGLE : undefined;

    // Where the map opens: her last region if she has one this app session,
    // otherwise a region capped to her own working area.
    //
    // The map mounts before the feed has loaded, so the opening region can't
    // just be computed here — `visits` is still empty on the first render and
    // that would open every nurse on the default Brooklyn centre. The value
    // below is only the placeholder the map is constructed with; the effect
    // further down applies the real one the first time a feed arrives, once.
    const initialRegion = useRef(lastViewedRegion ?? openingRegion(visits)).current;
    const openingApplied = useRef(lastViewedRegion != null);
    /** Read by the web pulse overlay's `draw`, which outlives this render. */
    const openingCenterRef = useRef(initialRegion);

    // The live region and the viewport it's drawn into. Both feed the
    // clustering, which is why the region is state and not just a ref: pins have
    // to re-merge and re-split as she zooms.
    const [region, setRegion] = useState<MapRegion>(initialRegion);
    const [viewport, setViewport] = useState<Viewport>({ width: 0, height: 0 });
    const [openCluster, setOpenCluster] = useState<VisitCluster | null>(null);
    // The map exists and will accept a camera move. Both the web map and the
    // native one come up asynchronously, and the opening region has to wait for
    // whichever lands last — the map or the first feed.
    const [mapReady, setMapReady] = useState(false);

    // Zoomed right out the map merges everything and says only how many; from
    // neighbourhood scale down it merges strictly within a colour, so a bubble
    // never mixes "yours" with "requested".
    const mode = clusterModeForRegion(region);
    const clusters = useMemo(
        () =>
            clusterPins(
                visits,
                region,
                viewport,
                mode === "grouped" ? { groupKey: pinColorKey } : undefined
            ),
        [visits, region, viewport, mode]
    );

    /** One entry point for "the map settled at a new region", shared by the
     *  native callback and the web `idle` listener. */
    const handleRegionSettled = useCallback((next: MapRegion) => {
        lastViewedRegion = next;
        setRegion((prev) => (regionChangedEnough(prev, next) ? next : prev));
    }, []);

    const closeOverlays = useCallback(() => {
        setOpenCluster(null);
        onPinClose();
    }, [onPinClose]);

    /** Move the camera. react-native-maps animates; the web map jumps, since
     *  Google's own `panTo` doesn't cover a zoom change.
     *
     *  `cover` on web is load-bearing: these moves exist to get *tighter* than
     *  the region, and rounding the zoom outwards instead would land wide
     *  enough to leave the cluster merged — the tap would visibly do nothing. */
    const flyTo = useCallback(
        (next: MapRegion) => {
            if (Platform.OS !== "web") {
                nativeMapRef.current?.animateToRegion(next, 300);
                return;
            }
            const map = mapInstanceRef.current as {
                setCenter?: (c: { lat: number; lng: number }) => void;
                setZoom?: (z: number) => void;
            } | null;
            map?.setCenter?.({ lat: next.latitude, lng: next.longitude });
            map?.setZoom?.(zoomForRegion(next, viewport, "cover"));
        },
        [viewport]
    );

    /**
     * A tap on a bubble, resolved against everything under her finger.
     *
     * The order matters and is the whole interaction:
     *   one visit there        → select it, card opens
     *   several, separable     → fly in; they split into colours, then into
     *                            individual pins, one tap per level
     *   several, inseparable   → the list sheet, because they share an address
     *                            and no zoom will ever put a gap between them
     *
     * `collidingClusters` is what makes the second and third cases cover
     * *neighbouring* bubbles too, not just the one that got the event — with
     * colour grouping, an amber bubble sitting on a green one is two clusters
     * but one tap target.
     */
    const handleClusterPress = useCallback(
        (cluster: VisitCluster) => {
            const touching = collidingClusters(clusters, cluster, region, viewport);
            const members = touching.flatMap((c) => c.members);

            if (members.length === 1) {
                setOpenCluster(null);
                onPinPress(members[0]);
                return;
            }

            const splitAt = clusterSplitRegion(members, viewport);
            if (splitAt) {
                setOpenCluster(null);
                onPinClose();
                flyTo(splitAt);
                return;
            }

            onPinClose();
            setOpenCluster({ ...cluster, members });
        },
        [clusters, region, viewport, onPinPress, onPinClose, flyTo]
    );

    const handleClusterRowSelect = useCallback(
        (visit: AvailableVisit) => {
            setOpenCluster(null);
            onPinPress(visit);
        },
        [onPinPress]
    );

    const handleNativeMapPress = useCallback(
        (event?: { nativeEvent?: { action?: string } }) => {
            if (event?.nativeEvent?.action === "marker-press") {
                return;
            }
            closeOverlays();
        },
        [closeOverlays]
    );

    const syncAndroidMarkerPoints = useCallback(async () => {
        if (Platform.OS !== "android" || !nativeMapRef.current?.pointForCoordinate) {
            return;
        }

        try {
            const projectedPoints = await Promise.all(
                clusters.map(async (cluster) => {
                    const point = await nativeMapRef.current?.pointForCoordinate?.({
                        latitude: cluster.lat,
                        longitude: cluster.lng
                    });

                    return point ? ([cluster.key, point] as const) : null;
                })
            );

            // Merge into existing points instead of replacing wholesale: a single
            // projection failure during map settle would otherwise wipe a marker
            // for good until the next pan/region change. Stale cluster keys are
            // pruned so removed/claimed visits don't leave ghost markers behind.
            const currentKeys = new Set(clusters.map((cluster) => cluster.key));
            setAndroidMarkerPoints((prev) => {
                const merged: Record<string, { x: number; y: number }> = {};
                for (const [key, point] of Object.entries(prev)) {
                    if (currentKeys.has(key)) {
                        merged[key] = point;
                    }
                }
                for (const entry of projectedPoints) {
                    if (entry) {
                        const [key, point] = entry;
                        merged[key] = { x: point.x, y: point.y };
                    }
                }
                return merged;
            });
        } catch {
            // Android map projections can briefly fail while the map is settling.
        }
    }, [clusters]);

    const scheduleAndroidMarkerSync = useCallback(() => {
        if (Platform.OS !== "android") {
            return;
        }

        if (androidOverlayRefreshTimeoutRef.current) {
            clearTimeout(androidOverlayRefreshTimeoutRef.current);
        }

        androidOverlayRefreshTimeoutRef.current = setTimeout(() => {
            void syncAndroidMarkerPoints();
        }, 40);
    }, [syncAndroidMarkerPoints]);

    /** MapKit (iOS) or Google (Android) settled after a pan or zoom: remember
     *  where she is, and re-cluster for the new scale. */
    const handleNativeRegionChange = useCallback(
        (next: MapRegion) => {
            handleRegionSettled(next);
            scheduleAndroidMarkerSync();
        },
        [handleRegionSettled, scheduleAndroidMarkerSync]
    );

    // Initialize interactive Google Map on web — once. Marker rendering is a
    // separate effect below so that a re-cluster (pan, zoom, refetch) does not
    // tear down and rebuild the map itself.
    useEffect(() => {
        if (Platform.OS !== "web" || !MAPS_KEY || mapInstanceRef.current) {
            return;
        }

        const initMap = () => {
            const el = mapRef.current as unknown as HTMLElement;
            if (!el) {
                return;
            }
            markersRef.current = [];
            const g = (window as unknown as Record<string, unknown>).google as {
                maps: {
                    Map: new (el: HTMLElement, opts: Record<string, unknown>) => unknown;
                    Marker: new (opts: Record<string, unknown>) => unknown;
                    OverlayView: unknown;
                    event: { addListener: (obj: unknown, event: string, cb: () => void) => void };
                };
            };

            const map = new g.maps.Map(el, {
                center: { lat: initialRegion.latitude, lng: initialRegion.longitude },
                // Was hardcoded to 13, which had web opening at a completely
                // different scale from the two native paths.
                zoom: zoomForRegion(initialRegion, {
                    width: el.clientWidth,
                    height: el.clientHeight
                }),
                disableDefaultUI: true,
                zoomControl: true,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                clickableIcons: false,
                styles: MAP_STYLES,
                gestureHandling: "greedy"
            });
            mapInstanceRef.current = map;
            setMapReady(true);

            g.maps.event.addListener(map, "click", () => {
                closeOverlays();
            });

            // Feed the same clustering the native paths use: whenever the map
            // settles, hand its bounds over as a region.
            g.maps.event.addListener(map, "idle", () => {
                const bounds = (
                    map as unknown as {
                        getBounds: () =>
                            | {
                                  getNorthEast: () => { lat: () => number; lng: () => number };
                                  getSouthWest: () => { lat: () => number; lng: () => number };
                              }
                            | undefined;
                    }
                ).getBounds();
                if (!bounds) {
                    return;
                }
                const ne = bounds.getNorthEast();
                const sw = bounds.getSouthWest();
                handleRegionSettled({
                    latitude: (ne.lat() + sw.lat()) / 2,
                    longitude: (ne.lng() + sw.lng()) / 2,
                    latitudeDelta: Math.abs(ne.lat() - sw.lat()),
                    longitudeDelta: Math.abs(ne.lng() - sw.lng())
                });
            });

            // Placeholder "you are here" pulse. It has never been a real
            // location fix — the feed carries no home coordinate — so it sits on
            // the map's opening centre.
            const rnMarkerEl = document.createElement("div");
            rnMarkerEl.innerHTML = `
                <div style="position:relative;width:48px;height:48px;display:flex;align-items:center;justify-content:center">
                    <div style="position:absolute;width:48px;height:48px;border-radius:24px;background:rgba(69,210,176,0.08);animation:pulse-ring 2.5s ease infinite"></div>
                    <div style="position:absolute;width:36px;height:36px;border-radius:18px;background:rgba(69,210,176,0.15)"></div>
                    <div style="width:22px;height:22px;border-radius:11px;background:white;box-shadow:0 2px 8px rgba(0,0,0,0.15);display:flex;align-items:center;justify-content:center;position:relative;z-index:1">
                        <div style="width:14px;height:14px;border-radius:7px;background:#45D2B0;display:flex;align-items:center;justify-content:center">
                            <div style="width:6px;height:6px;border-radius:3px;background:white"></div>
                        </div>
                    </div>
                </div>
            `;
            if (!document.getElementById("map-pulse-style")) {
                const style = document.createElement("style");
                style.id = "map-pulse-style";
                style.textContent =
                    "@keyframes pulse-ring { 0% { transform: scale(1); opacity: 0.3; } 100% { transform: scale(1.5); opacity: 0; } }";
                document.head.appendChild(style);
            }

            const OverlayView = g.maps.OverlayView as unknown as {
                new (): {
                    setMap: (map: unknown) => void;
                    onAdd: () => void;
                    draw: () => void;
                    onRemove: () => void;
                    getProjection: () => {
                        fromLatLngToDivPixel: (ll: unknown) => { x: number; y: number };
                    };
                    getPanes: () => { overlayMouseTarget: HTMLElement };
                };
                prototype: unknown;
            };

            const rnOverlay = new OverlayView();
            rnOverlay.onAdd = function () {
                this.getPanes().overlayMouseTarget.appendChild(rnMarkerEl);
            };
            rnOverlay.draw = function () {
                const proj = this.getProjection();
                const LatLng = (g.maps as unknown as Record<string, unknown>).LatLng as new (
                    lat: number,
                    lng: number
                ) => unknown;
                const pos = proj.fromLatLngToDivPixel(
                    new LatLng(
                        openingCenterRef.current.latitude,
                        openingCenterRef.current.longitude
                    )
                );
                rnMarkerEl.style.position = "absolute";
                rnMarkerEl.style.left = `${pos.x - 24}px`;
                rnMarkerEl.style.top = `${pos.y - 24}px`;
            };
            rnOverlay.onRemove = function () {
                rnMarkerEl.remove();
            };
            rnOverlay.setMap(map);
        };

        void ensureMapsLoaded().then(() => setTimeout(initMap, 100));
    }, [MAPS_KEY, initialRegion, closeOverlays, handleRegionSettled]);

    // Draw the cluster bubbles on web. Separate from map creation so panning,
    // zooming, selecting and refetching all just redraw markers.
    useEffect(() => {
        if (Platform.OS !== "web" || !mapInstanceRef.current) {
            return;
        }

        const g = (window as unknown as Record<string, unknown>).google as
            | {
                  maps: {
                      OverlayView: unknown;
                      LatLng: new (lat: number, lng: number) => unknown;
                  };
              }
            | undefined;
        if (!g) {
            return;
        }

        const map = mapInstanceRef.current;
        const OverlayView = g.maps.OverlayView as unknown as {
            new (): {
                setMap: (map: unknown) => void;
                onAdd: () => void;
                draw: () => void;
                onRemove: () => void;
                getProjection: () => {
                    fromLatLngToDivPixel: (ll: unknown) => { x: number; y: number };
                };
                getPanes: () => { overlayMouseTarget: HTMLElement };
            };
        };

        const drawn = clusters.map((cluster) => {
            const lead = clusterLead(cluster.members);
            const count = cluster.members.length;
            const vtColor = visitTypeColors[lead.visitType]?.text ?? colors.teal400;
            const bubbleBg = relationStyle(lead).pin;
            const isSelected =
                (count === 1 && selectedPin?.id === cluster.members[0].id) ||
                openCluster?.key === cluster.key;

            const markerEl = document.createElement("div");
            markerEl.style.cursor = "pointer";

            // Overview: a neutral circle carrying only the count. Same rule as
            // the native paths — no colour, no pay, nothing a merged marker
            // cannot honestly claim at this scale.
            const circleSize = countCircleSize(count);
            const isOverview = mode === "overview";
            if (isOverview) {
                markerEl.innerHTML = `
                    <div style="width:${circleSize}px;height:${circleSize}px;border-radius:50%;background:${colors.gray800};border:3px solid ${
                        isSelected ? "#45D2B0" : "rgba(255,255,255,0.92)"
                    };display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(0,0,0,0.2)">
                        <span style="font-family:Plus Jakarta Sans,sans-serif;font-size:${
                            circleSize >= 56 ? 20 : 17
                        }px;font-weight:800;color:white">${count}</span>
                    </div>
                `;
            }

            // Unseen dot rides on the bubble's top-right corner with a white
            // ring so it stays visible against every pin colour.
            const unseenDot = clusterHasUnseen(cluster.members)
                ? // border-radius must be 50%, not 5px: the 2px ring sits
                  // OUTSIDE the 10px box on web, so a fixed 5px radius on the
                  // resulting 14px box renders a rounded square, not a dot.
                  `<div style="position:absolute;top:-3px;right:-3px;width:10px;height:10px;border-radius:50%;background:${exploreUnseenDot};border:2px solid #fff"></div>`
                : "";
            const leadMark =
                count > 1
                    ? `<div style="min-width:18px;height:18px;border-radius:9px;background:rgba(255,255,255,0.22);display:flex;align-items:center;justify-content:center;padding:0 4px"><span style="font-family:Plus Jakarta Sans,sans-serif;font-size:11px;font-weight:800;color:white">${count}</span></div>`
                    : `<div style="width:7px;height:7px;border-radius:4px;background:${vtColor}"></div>`;
            // Selection reads as a teal ring + lift, never a repaint: the fill
            // carries her relationship to the visit and losing it on tap is
            // exactly when she is looking hardest.
            const shadow = isSelected
                ? "0 0 0 3px #45D2B0, 0 6px 16px rgba(69,210,176,0.4)"
                : "0 4px 12px rgba(0,0,0,0.15)";
            if (!isOverview) {
                markerEl.innerHTML = `
                    <div style="display:flex;flex-direction:column;align-items:center">
                        <div style="position:relative;display:flex;align-items:center;gap:5px;background:${bubbleBg};padding:6px 10px;border-radius:18px;box-shadow:${shadow}">
                            ${leadMark}
                            <span style="font-family:Plus Jakarta Sans,sans-serif;font-size:14px;font-weight:800;color:white">${clusterPriceLabel(cluster.members)}</span>
                            ${unseenDot}
                        </div>
                        <div style="width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:6px solid ${bubbleBg};margin-top:-1px"></div>
                    </div>
                `;
            }

            markerEl.addEventListener("click", (e) => {
                e.stopPropagation();
                handleClusterPress(cluster);
            });

            const overlay = new OverlayView();
            overlay.onAdd = function () {
                this.getPanes().overlayMouseTarget.appendChild(markerEl);
            };
            overlay.draw = function () {
                const proj = this.getProjection();
                const pos = proj.fromLatLngToDivPixel(new g.maps.LatLng(cluster.lat, cluster.lng));
                markerEl.style.position = "absolute";
                // A circle centres on its coordinate; a bubble hangs above it,
                // with its arrow tip on the point.
                markerEl.style.left = `${pos.x - (isOverview ? circleSize / 2 : 30)}px`;
                markerEl.style.top = `${pos.y - (isOverview ? circleSize / 2 : 40)}px`;
                markerEl.style.zIndex = String(clusterZIndex(cluster.members, isSelected));
            };
            overlay.onRemove = function () {
                markerEl.remove();
            };
            overlay.setMap(map);
            return { overlay, el: markerEl };
        });

        markersRef.current = drawn;

        return () => {
            for (const marker of drawn) {
                marker.overlay.setMap(null);
            }
        };
    }, [clusters, mode, selectedPin, openCluster, handleClusterPress]);

    // Apply the opening region the first time a feed lands. Deliberately once:
    // after this the map is hers to pan, and every later refetch must leave it
    // exactly where she put it.
    useEffect(() => {
        if (openingApplied.current || visits.length === 0 || !mapReady) {
            return;
        }
        openingApplied.current = true;

        const opening = openingRegion(visits);
        lastViewedRegion = opening;
        openingCenterRef.current = opening;
        setRegion(opening);

        if (Platform.OS !== "web") {
            nativeMapRef.current?.animateToRegion(opening, 0);
            return;
        }
        const map = mapInstanceRef.current as {
            setCenter?: (c: { lat: number; lng: number }) => void;
            setZoom?: (z: number) => void;
        } | null;
        map?.setCenter?.({ lat: opening.latitude, lng: opening.longitude });
        map?.setZoom?.(zoomForRegion(opening, viewport));
    }, [visits, viewport, mapReady]);

    useEffect(() => {
        if (Platform.OS === "web" || !selectedPin || !nativeMapRef.current) {
            return;
        }

        nativeMapRef.current.animateToRegion(selectedRegion(selectedPin), 250);
    }, [selectedPin]);

    // Keep the open sheet pointing at live data. Resolved by member id rather
    // than by cluster key: the sheet can hold visits gathered from several
    // touching bubbles, so there is no single cluster to look up. A sheet whose
    // visits have been claimed away until one is left has nothing to
    // disambiguate any more.
    useEffect(() => {
        if (!openCluster) {
            return;
        }
        const live = new Map(visits.map((visit) => [visit.id, visit]));
        const members = openCluster.members
            .map((member) => live.get(member.id))
            .filter((visit): visit is AvailableVisit => visit != null);

        if (members.length < 2) {
            setOpenCluster(null);
            return;
        }
        if (
            members.length !== openCluster.members.length ||
            members.some((member, i) => member !== openCluster.members[i])
        ) {
            setOpenCluster({ ...openCluster, members });
        }
    }, [visits, openCluster]);

    useEffect(() => {
        scheduleAndroidMarkerSync();
    }, [scheduleAndroidMarkerSync, clusters]);

    useEffect(() => {
        return () => {
            if (androidOverlayRefreshTimeoutRef.current) {
                clearTimeout(androidOverlayRefreshTimeoutRef.current);
            }
        };
    }, []);

    return (
        <View
            style={styles.mapContainer}
            onLayout={(event) => {
                const { width, height } = event.nativeEvent.layout;
                setViewport((prev) =>
                    prev.width === width && prev.height === height ? prev : { width, height }
                );
            }}
        >
            {/* Interactive Google Map (web) or fallback */}
            {Platform.OS === "web" && MAPS_KEY ? (
                <View ref={mapRef} style={StyleSheet.absoluteFill} {...logRocketRedactProps()} />
            ) : NativeMapView && Marker ? (
                <NativeMapView
                    {...logRocketRedactProps()}
                    ref={nativeMapRef}
                    style={StyleSheet.absoluteFill}
                    initialRegion={initialRegion}
                    provider={provider}
                    onMapReady={() => {
                        setMapReady(true);
                        scheduleAndroidMarkerSync();
                    }}
                    onPress={handleNativeMapPress}
                    onRegionChangeComplete={handleNativeRegionChange}
                    toolbarEnabled={false}
                    showsCompass={false}
                    showsMyLocationButton={false}
                    showsUserLocation={false}
                    moveOnMarkerPress={false}
                    mapPadding={{ top: 180, right: 24, bottom: 120, left: 24 }}
                >
                    {Platform.OS === "ios"
                        ? clusters.map((cluster) => {
                              const isSelected =
                                  (cluster.members.length === 1 &&
                                      selectedPin?.id === cluster.members[0].id) ||
                                  openCluster?.key === cluster.key;

                              return (
                                  <Marker
                                      key={cluster.key}
                                      coordinate={{
                                          latitude: cluster.lat,
                                          longitude: cluster.lng
                                      }}
                                      onSelect={() => handleClusterPress(cluster)}
                                      // A circle centres on its coordinate; a
                                      // bubble hangs above it with its arrow on
                                      // the point.
                                      anchor={
                                          mode === "overview"
                                              ? { x: 0.5, y: 0.5 }
                                              : { x: 0.5, y: 1 }
                                      }
                                      // The one thing that decides which bubble
                                      // takes the tap when two still overlap.
                                      // Never passed before this change, which
                                      // left every annotation at zPriority 0 and
                                      // the order up to MapKit.
                                      zIndex={clusterZIndex(cluster.members, isSelected)}
                                      tracksViewChanges
                                  >
                                      {mode === "overview" ? (
                                          <NativeCountCircle
                                              cluster={cluster}
                                              isSelected={isSelected}
                                          />
                                      ) : (
                                          <NativeMarkerBubble
                                              cluster={cluster}
                                              isSelected={isSelected}
                                              platformStyle={styles.nativeMarkerBubbleIos}
                                          />
                                      )}
                                  </Marker>
                              );
                          })
                        : null}
                </NativeMapView>
            ) : (
                <LinearGradient
                    colors={["#E8ECF0", "#F0F2F5", "#E5E8ED"]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={StyleSheet.absoluteFill}
                />
            )}

            {Platform.OS === "android" ? (
                <View pointerEvents="box-none" style={styles.androidMarkerOverlay}>
                    {/* Sorted so the most urgent bubble is painted last: this
                        overlay is plain absolutely-positioned views, where the
                        later sibling both draws on top and takes the tap. It is
                        the Android half of what `zIndex` does for the iOS
                        annotations. */}
                    {[...clusters]
                        .sort(
                            (a, b) =>
                                clusterZIndex(a.members, openCluster?.key === a.key) -
                                clusterZIndex(b.members, openCluster?.key === b.key)
                        )
                        .map((cluster) => {
                            const point = androidMarkerPoints[cluster.key];

                            if (!point) {
                                return null;
                            }

                            const isSelected =
                                (cluster.members.length === 1 &&
                                    selectedPin?.id === cluster.members[0].id) ||
                                openCluster?.key === cluster.key;
                            const circleSize = countCircleSize(cluster.members.length);
                            const isOverview = mode === "overview";

                            return (
                                <TouchableOpacity
                                    key={cluster.key}
                                    activeOpacity={0.9}
                                    onPress={() => handleClusterPress(cluster)}
                                    style={[
                                        styles.androidMarkerTouchable,
                                        // A circle centres on its coordinate;
                                        // a bubble hangs above it.
                                        isOverview
                                            ? {
                                                  left: point.x - circleSize / 2,
                                                  top: point.y - circleSize / 2
                                              }
                                            : { left: point.x - 46, top: point.y - 42 }
                                    ]}
                                >
                                    {isOverview ? (
                                        <NativeCountCircle
                                            cluster={cluster}
                                            isSelected={isSelected}
                                        />
                                    ) : (
                                        <NativeMarkerBubble
                                            cluster={cluster}
                                            isSelected={isSelected}
                                            platformStyle={styles.nativeMarkerBubbleAndroid}
                                        />
                                    )}
                                </TouchableOpacity>
                            );
                        })}
                </View>
            ) : null}

            {/* What is stacked on one spot, when a bubble stands for several
                visits. Takes precedence over the single-pin card — tapping a row
                closes it and selects that pin. */}
            {openCluster ? (
                <MapClusterSheet
                    cluster={openCluster}
                    onSelect={handleClusterRowSelect}
                    onClose={() => setOpenCluster(null)}
                />
            ) : selectedPin ? (
                <MapPinCard
                    visit={selectedPin}
                    onViewMore={() => onViewMore(selectedPin)}
                    onClose={onPinClose}
                />
            ) : null}
        </View>
    );
}

// ═══════════════════════════════════════════════════
// Cards View
// ═══════════════════════════════════════════════════

function CardsView({
    visits,
    onCardPress
}: {
    visits: AvailableVisit[];
    onCardPress: (v: AvailableVisit) => void;
}) {
    const renderItem = useCallback(
        ({ item }: { item: AvailableVisit }) => (
            <VisitCard visit={item} onPress={() => onCardPress(item)} />
        ),
        [onCardPress]
    );

    return (
        <FlatList
            data={visits}
            renderItem={renderItem}
            keyExtractor={(item) => String(item.id)}
            contentContainerStyle={styles.cardsList}
            showsVerticalScrollIndicator={false}
            ItemSeparatorComponent={() => <View style={{ height: 12 }} />}
        />
    );
}

// ═══════════════════════════════════════════════════
// Main Screen
// ═══════════════════════════════════════════════════

export default function ExploreScreen() {
    const router = useRouter();
    const { user, logout } = useAuth();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [viewMode, setViewMode] = useState<"map" | "cards">("map");
    const [headerHeight, setHeaderHeight] = useState(0);
    const [selectedPin, setSelectedPin] = useState<AvailableVisit | null>(null);
    // Deduped: a background refetch that returns the same list must not
    // re-render the map + lists.
    const [visits, setVisits] = useDedupedState<AvailableVisit[]>([]);
    const [myVisits, setMyVisits] = useDedupedState<VisitListItem[]>([]);
    const [myRequests, setMyRequests] = useDedupedState<VisitRequestListItem[]>([]);
    const [requestsPanelOpen, setRequestsPanelOpen] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [onboardingUnderReview, setOnboardingUnderReview] = useState(false);

    const rnId = user?.rnId ? String(user.rnId) : null;

    // Fetch available visits from API — silently, in place: no loading state,
    // the screen keeps showing what it has until fresh data lands.
    const loadVisits = useCallback(() => {
        return fetchAvailableVisits()
            .then(setVisits)
            .catch(() => setVisits([]));
    }, [setVisits]);

    const loadMyVisits = useCallback(() => {
        return fetchVisits()
            .then(setMyVisits)
            .catch(() => setMyVisits([]));
    }, [setMyVisits]);

    // The nurse's own pending/cancelled/rejected requests, for the "My Requests"
    // panel. Kept separate from `myVisits` (assigned visits) — a request has no
    // assigned visit yet.
    const loadMyRequests = useCallback(() => {
        return fetchVisitRequests()
            .then(setMyRequests)
            .catch(() => setMyRequests([]));
    }, [setMyRequests]);

    // WS: any change to the available pool refetches the list.
    // - broadcasted/unbroadcasted reach every RN (rn:all)
    // - assigned only reaches the assignee — they get it when they claim a visit,
    //   so we drop it from their own Explore list. Other RNs see the same flow
    //   surface as unbroadcasted on rn:all.
    // Coalesced, not debounced: during a broadcast burst `rn:all` delivers an
    // event every ~1–3 s for the whole burst, so a trailing 300 ms debounce
    // never collapsed anything and the screen refetched per event (the "25 s
    // to load" incident, 2026-07-01). Now at most one request per list is in
    // flight and at most one more is queued — events during a run collapse
    // into that single follow-up.
    const refreshVisits = useCoalescedRefetch(loadVisits);
    const refreshMyVisits = useCoalescedRefetch(loadMyVisits);
    const refreshMyRequests = useCoalescedRefetch(loadMyRequests);

    useEffect(() => {
        refreshVisits();
        refreshMyRequests();
    }, [refreshVisits, refreshMyRequests]);

    // This tab stays mounted beneath visit screens. Reload RN-owned visits
    // on return so a submitted clarification removes its resubmission banner,
    // even when no websocket event was delivered.
    useFocusEffect(
        useCallback(() => {
            refreshMyVisits();
        }, [refreshMyVisits])
    );

    useWebSocketEvent("visit:updated", (data) => {
        const { action } = data;
        if (action === "broadcasted" || action === "unbroadcasted" || action === "assigned") {
            refreshVisits();
            refreshMyVisits();
        }
        // Refresh the "My Requests" panel whenever a request changes state.
        // (`unbroadcasted` fires when this RN requests a visit; the others when
        // the agency/bot accepts, rejects, or cancels a request.)
        if (
            action === "unbroadcasted" ||
            action === "request_accepted" ||
            action === "request_rejected" ||
            action === "request_cancelled"
        ) {
            refreshMyRequests();
        }
    });

    // Check if RN needs onboarding (hide for active RNs with completed onboarding)
    useEffect(() => {
        if (!rnId) {
            return;
        }
        // Already-active RNs have finished onboarding and admin review — never prompt them.
        if (user?.rnStatus === "active") {
            setShowOnboarding(false);
            setOnboardingUnderReview(false);
            return;
        }
        fetchOnboardingProgress(rnId)
            .then((progress) => {
                // allSubmitted = she has handed everything in. Show "under
                // review" instead of a dead-end (the old behavior hid the pill
                // entirely once complete, leaving no explanation of what's
                // going on until activation).
                setShowOnboarding(!progress.allSubmitted);
                setOnboardingUnderReview(progress.allSubmitted);
            })
            .catch(() => {
                // On error, don't show onboarding pill — avoid false prompts for active RNs
                setShowOnboarding(false);
                setOnboardingUnderReview(false);
            });
    }, [rnId, user?.rnStatus]);

    const firstName = useMemo(() => {
        return user?.firstName ?? user?.name?.split(" ")[0] ?? "";
    }, [user?.firstName, user?.name]);

    const { scheduledCount, pendingTodayCount } = useMemo(() => {
        const now = new Date();
        const todayPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
        const todays = myVisits.filter(
            (v) => v.startTime != null && v.startTime.slice(0, 10) === todayPrefix
        );
        const pending = todays.filter(
            (v) => v.status !== "completed" && v.status !== "sealed"
        ).length;
        return { scheduledCount: todays.length, pendingTodayCount: pending };
    }, [myVisits]);
    const pendingCount = visits.length;
    // Pending-only count drives the amber badge on the "My Requests" button.
    const pendingRequestCount = useMemo(
        () => myRequests.filter((r) => r.status === "pending").length,
        [myRequests]
    );
    const reopenedVisits = useMemo(
        () => myVisits.filter((visit) => visit.openForResubmission),
        [myVisits]
    );

    const greeting = useMemo(() => {
        const h = new Date().getHours();
        if (h < 12) {
            return "Good morning";
        }
        if (h < 17) {
            return "Good afternoon";
        }
        return "Good evening";
    }, []);

    const toggleView = useCallback(() => {
        setSelectedPin(null);
        setViewMode((prev) => (prev === "map" ? "cards" : "map"));
    }, []);

    const handlePinPress = useCallback((visit: AvailableVisit) => {
        setSelectedPin((prev) => (prev?.id === visit.id ? null : visit));
    }, []);

    /**
     * Drop the "new to you" dot the moment she opens a visit.
     *
     * The detail screen POSTs the real view record, but coming back from it does
     * NOT remount this tab — the screen only refetches on mount and on WebSocket
     * `visit:updated` events — so without this the dot survived until a manual
     * refresh or a tab switch. This is the optimistic half; the server record is
     * still what survives a real refetch, so a failed POST just brings the dot
     * back next time rather than leaving a permanent lie.
     */
    const markVisitSeenLocally = useCallback(
        (visitId: number) => {
            const now = new Date().toISOString();
            setVisits(
                visits.map((v) =>
                    v.id === visitId && v.viewed == null
                        ? {
                              ...v,
                              viewed: { firstViewedAt: now, lastViewedAt: now, viewCount: 1 }
                          }
                        : v
                )
            );
        },
        // `visits` is a dep because useDedupedState's setter takes a value, not
        // an updater. Re-mapping an already-seen visit produces a deep-equal
        // array, which the deduped setter drops — so this can't cause a render
        // loop or a pointless map re-init.
        [visits, setVisits]
    );

    const handleViewMore = useCallback(
        (visit: AvailableVisit) => {
            // A visit already assigned to her is no longer a broadcast offer:
            // `GET /available-visits/:id` 404s on it (the broadcast/pending
            // gate), so send her to the real visit screen instead. That screen
            // doesn't record a view, which is fine — an assigned visit never
            // carries the dot in the first place (see `isUnseen`).
            if (visit.relation === "assigned") {
                router.push(`/visit/${visit.id}`);
                return;
            }
            markVisitSeenLocally(visit.id);
            router.push(`/available-visit/${visit.id}`);
        },
        [router, markVisitSeenLocally]
    );

    return (
        <View style={styles.container}>
            {/* Main content */}
            {viewMode === "map" ? (
                <MapView
                    visits={visits}
                    selectedPin={selectedPin}
                    onPinPress={handlePinPress}
                    onPinClose={() => setSelectedPin(null)}
                    onViewMore={handleViewMore}
                />
            ) : (
                <View style={styles.cardsContainer}>
                    <View style={{ height: headerHeight || 170 }} />
                    <CardsView visits={visits} onCardPress={handleViewMore} />
                </View>
            )}

            {/* ── Floating Header ── */}
            <SafeAreaView
                edges={["top"]}
                style={styles.headerSafe}
                onLayout={(e) => {
                    const h = e.nativeEvent.layout.height;
                    if (h > 0 && Math.abs(h - headerHeight) > 0.5) {
                        setHeaderHeight(h);
                    }
                }}
            >
                <View style={styles.headerCard}>
                    <View style={styles.headerRow}>
                        <TouchableOpacity
                            activeOpacity={0.7}
                            style={styles.logoPill}
                            onPress={() => setDrawerOpen(true)}
                        >
                            <PinwheelLogo size={34} />
                            <View>
                                <Text style={styles.logoText}>TaskHealth</Text>
                                <Text style={styles.logoSub}>RN Field App</Text>
                            </View>
                        </TouchableOpacity>
                        {pendingCount > 0 && (
                            <LinearGradient
                                colors={[colors.error400, colors.error500]}
                                start={{ x: 0, y: 0 }}
                                end={{ x: 1, y: 1 }}
                                style={styles.badgeCount}
                            >
                                <Text style={styles.badgeCountText}>{pendingCount} Open</Text>
                            </LinearGradient>
                        )}
                    </View>
                    <View style={styles.greetingRow}>
                        <View>
                            <Text style={styles.greetingHello}>{greeting},</Text>
                            <Text style={styles.greetingName}>{firstName}</Text>
                        </View>
                        {showOnboarding && rnId ? (
                            <TouchableOpacity
                                activeOpacity={0.7}
                                onPress={() => router.push(`/onboarding?rnId=${rnId}`)}
                            >
                                <LinearGradient
                                    colors={[...gradients.brand]}
                                    start={{ x: 0, y: 0 }}
                                    end={{ x: 1, y: 1 }}
                                    style={styles.orientationPill}
                                >
                                    <MaterialCommunityIcons
                                        name="clipboard-check-outline"
                                        size={14}
                                        color="#FFFFFF"
                                    />
                                    <Text style={styles.orientationPillText}>
                                        Complete onboarding
                                    </Text>
                                    <MaterialCommunityIcons
                                        name="chevron-right"
                                        size={14}
                                        color="#FFFFFF"
                                    />
                                </LinearGradient>
                            </TouchableOpacity>
                        ) : onboardingUnderReview && rnId ? (
                            <TouchableOpacity
                                activeOpacity={0.7}
                                onPress={() => router.push(`/onboarding?rnId=${rnId}`)}
                                style={styles.underReviewPill}
                            >
                                <MaterialCommunityIcons
                                    name="clipboard-clock-outline"
                                    size={14}
                                    color={colors.teal600}
                                />
                                <Text style={styles.underReviewPillText}>
                                    Application under review
                                </Text>
                                <MaterialCommunityIcons
                                    name="chevron-right"
                                    size={14}
                                    color={colors.teal600}
                                />
                            </TouchableOpacity>
                        ) : (
                            <Text style={styles.greetingSub}>
                                {scheduledCount > 0
                                    ? `${scheduledCount} visits today (${pendingTodayCount} pending)`
                                    : `${pendingCount} pending visits for you`}
                            </Text>
                        )}
                    </View>
                    {/*  "Best for you" — ranked open visits pre-verified to fit
                        her schedule.

                        Hidden for applicants mid-onboarding. Browsing the map is
                        an invitation; Smart Picks is a booking tool, and offering
                        it before activation only leads to a 403.

                        Tested for the applicant state specifically, NOT for
                        `=== "active"`: `rnStatus` is device-cached and older
                        sessions carry none at all, so requiring "active" hid
                        Smart Picks from long-active nurses. */}
                    {pendingCount > 0 && user?.rnStatus !== "pending_application" && (
                        <TouchableOpacity
                            activeOpacity={0.85}
                            onPress={() => router.push("/suggestions")}
                        >
                            <LinearGradient
                                colors={[...gradients.brandSoft]}
                                start={{ x: 0, y: 0 }}
                                end={{ x: 1, y: 0 }}
                                style={styles.suggestionsButton}
                            >
                                <LinearGradient
                                    colors={[...gradients.brand]}
                                    start={{ x: 0, y: 0 }}
                                    end={{ x: 1, y: 1 }}
                                    style={styles.suggestionsButtonIcon}
                                >
                                    <MaterialCommunityIcons
                                        name="auto-fix"
                                        size={14}
                                        color="#FFFFFF"
                                    />
                                </LinearGradient>
                                <View style={{ flex: 1 }}>
                                    <Text style={styles.suggestionsButtonTitle}>Smart Picks</Text>
                                    <Text style={styles.suggestionsButtonSub}>
                                        Visits picked to fit your week — just ask
                                    </Text>
                                </View>
                                <MaterialCommunityIcons
                                    name="chevron-right"
                                    size={16}
                                    color={colors.textTertiary}
                                />
                            </LinearGradient>
                        </TouchableOpacity>
                    )}
                    {myRequests.length > 0 && (
                        <TouchableOpacity
                            activeOpacity={0.8}
                            style={styles.requestsButton}
                            onPress={() => setRequestsPanelOpen(true)}
                        >
                            <MaterialCommunityIcons
                                name="clipboard-text-clock-outline"
                                size={16}
                                color={colors.teal400}
                            />
                            <Text style={styles.requestsButtonText}>My Requests</Text>
                            {/* Amber badge only when there are pending offers — it
                                counts pending only, not approved/cancelled/rejected. */}
                            {pendingRequestCount > 0 && (
                                <View style={styles.requestsButtonCount}>
                                    <Text style={styles.requestsButtonCountText}>
                                        {pendingRequestCount}
                                    </Text>
                                </View>
                            )}
                            <MaterialCommunityIcons
                                name="chevron-right"
                                size={16}
                                color={colors.textTertiary}
                            />
                        </TouchableOpacity>
                    )}
                    <ComplianceBanner />
                    {reopenedVisits.length > 0 ? (
                        <View style={styles.resubmissionBanner}>
                            <View style={styles.resubmissionBannerHeader}>
                                <MaterialCommunityIcons
                                    name="file-document-edit-outline"
                                    size={18}
                                    color={colors.teal600}
                                />
                                <Text style={styles.resubmissionBannerTitle}>
                                    {reopenedVisits.length === 1
                                        ? "Visit ready for resubmission"
                                        : `${reopenedVisits.length} visits ready for resubmission`}
                                </Text>
                            </View>
                            {reopenedVisits.slice(0, 3).map((visit) => (
                                <TouchableOpacity
                                    key={visit.id}
                                    activeOpacity={0.75}
                                    style={styles.resubmissionVisitRow}
                                    onPress={() => router.push(`/visit/${visit.id}`)}
                                >
                                    <Text style={styles.resubmissionVisitName} numberOfLines={1}>
                                        {visit.patientFirstName} {visit.patientLastName}
                                    </Text>
                                    <MaterialCommunityIcons
                                        name="chevron-right"
                                        size={16}
                                        color={colors.textTertiary}
                                    />
                                </TouchableOpacity>
                            ))}
                        </View>
                    ) : null}
                </View>
            </SafeAreaView>

            {/* ── Floating Action Button (right side) ── */}
            <View style={[styles.fabColumn, selectedPin && { display: "none" }]}>
                {/* Map / Cards toggle */}
                <TouchableOpacity activeOpacity={0.8} onPress={toggleView}>
                    <LinearGradient
                        colors={[...gradients.brand]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={styles.fabPrimary}
                    >
                        <MaterialCommunityIcons
                            name={viewMode === "map" ? "view-list" : "map"}
                            size={24}
                            color="#FFFFFF"
                        />
                    </LinearGradient>
                </TouchableOpacity>
            </View>

            {/* ── My Requests Panel ── */}
            <MyRequestsPanel
                visible={requestsPanelOpen}
                requests={myRequests}
                onClose={() => setRequestsPanelOpen(false)}
                onSelect={(request) => {
                    // Only requests whose visit is still hers are tappable; route
                    // pending → available-visit detail, approved → her visit.
                    const route = requestVisitRoute(request);
                    if (route) {
                        setRequestsPanelOpen(false);
                        router.push(route);
                    }
                }}
            />

            {/* ── Profile Drawer ── */}
            <ProfileDrawer
                visible={drawerOpen}
                user={user}
                onClose={() => setDrawerOpen(false)}
                onLogout={logout}
            />
        </View>
    );
}

// ═══════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background
    },

    // ── Header ──
    headerSafe: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 20
    },
    headerCard: {
        marginHorizontal: spacing.base,
        marginTop: spacing.sm,
        paddingHorizontal: spacing.lg,
        paddingVertical: spacing.base,
        backgroundColor: "rgba(255,255,255,0.95)",
        borderRadius: radius.xl,
        borderWidth: 1.5,
        borderColor: colors.border,
        ...shadows.lg
    },
    headerRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: spacing.md
    },
    logoPill: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm
    },
    logoText: {
        ...typography.title,
        color: colors.textPrimary
    },
    logoSub: {
        ...typography.label,
        color: colors.textTertiary,
        letterSpacing: 0.3
    },
    badgeCount: {
        paddingHorizontal: spacing.md,
        paddingVertical: 5,
        borderRadius: radius.full
    },
    badgeCountText: {
        ...typography.micro,
        color: "#FFFFFF",
        letterSpacing: 0.5
    },
    greetingRow: {
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "space-between"
    },
    greetingHello: {
        ...typography.body,
        color: colors.textSecondary
    },
    greetingName: {
        ...typography.h1,
        color: colors.textPrimary,
        marginTop: 2
    },
    greetingSub: {
        ...typography.caption,
        color: colors.textTertiary
    },
    orientationPill: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: radius.full,
        ...shadows.brand
    },
    underReviewPill: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: radius.full,
        backgroundColor: colors.teal50,
        borderWidth: 1,
        borderColor: colors.teal200
    },
    underReviewPillText: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors.teal600
    },
    orientationPillText: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    },
    suggestionsButton: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        marginTop: spacing.md,
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: colors.border
    },
    suggestionsButtonIcon: {
        width: 26,
        height: 26,
        borderRadius: radius.full,
        alignItems: "center",
        justifyContent: "center"
    },
    suggestionsButtonTitle: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    suggestionsButtonSub: {
        fontSize: 11,
        fontFamily: fonts.medium,
        color: colors.textSecondary
    },
    requestsButton: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginTop: spacing.md,
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderRadius: radius.md,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border
    },
    requestsButtonText: {
        flex: 1,
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    requestsButtonCount: {
        minWidth: 22,
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: radius.full,
        backgroundColor: colors.warning500,
        alignItems: "center"
    },
    requestsButtonCountText: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    },

    // ── Map ──
    mapContainer: {
        flex: 1
    },
    androidMarkerOverlay: {
        ...StyleSheet.absoluteFill,
        zIndex: 6
    },
    androidMarkerTouchable: {
        position: "absolute"
    },
    nativeMarkerWrap: {
        alignItems: "center",
        minWidth: 88,
        paddingHorizontal: 8,
        paddingTop: 6,
        paddingBottom: 10
    },
    nativeMarkerBubble: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: "#0B2D4D",
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 18
    },
    nativeMarkerBubbleIos: {
        ...shadows.md
    },
    nativeMarkerBubbleAndroid: {
        ...shadows.md
    },
    // Selection is a teal ring, not a teal fill — the fill carries the nurse's
    // relationship to the visit (requested / rejected / hers) and must survive
    // being tapped.
    nativeMarkerBubbleSelected: {
        borderWidth: 2,
        borderColor: colors.teal400
    },
    nativeMarkerDot: {
        width: 7,
        height: 7,
        borderRadius: 4,
        marginRight: 5
    },
    // ── Overview marker (zoomed out past ~25 miles) ──
    // Deliberately neutral: no relation colour, no pay, no unseen dot. A marker
    // this far out stands for a mixed pile and can't claim any of them.
    nativeCircleWrap: {
        alignItems: "center",
        justifyContent: "center",
        padding: 4
    },
    nativeCountCircle: {
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: colors.gray800,
        borderWidth: 3,
        borderColor: "rgba(255,255,255,0.92)",
        ...shadows.md
    },
    nativeCountCircleSelected: {
        borderColor: colors.teal400
    },
    nativeCountCircleText: {
        fontFamily: fonts.extraBold,
        color: "#FFFFFF"
    },

    // Replaces the visit-type dot on a merged bubble: the count is the thing she
    // needs to know is there, and one bubble can't honestly carry one type.
    nativeMarkerCount: {
        minWidth: 18,
        height: 18,
        borderRadius: 9,
        paddingHorizontal: 4,
        marginRight: 5,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(255,255,255,0.22)"
    },
    nativeMarkerCountText: {
        fontSize: 11,
        fontFamily: fonts.extraBold,
        color: "#FFFFFF"
    },
    nativeMarkerText: {
        fontSize: 14,
        fontWeight: "800",
        color: "#FFFFFF"
    },
    // "Never opened" dot, pinned to the bubble's top-right corner. White ring so
    // it separates from whichever relation colour is behind it.
    nativeMarkerUnseen: {
        position: "absolute",
        top: -3,
        right: -3,
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: exploreUnseenDot,
        borderWidth: 2,
        borderColor: "#FFFFFF"
    },
    nativeMarkerArrow: {
        width: 0,
        height: 0,
        borderLeftWidth: 6,
        borderRightWidth: 6,
        borderTopWidth: 6,
        borderLeftColor: "transparent",
        borderRightColor: "transparent",
        borderTopColor: "#0B2D4D",
        marginTop: -1
    },

    // ── Pin Card Popup ──
    pinCardWrapper: {
        position: "absolute",
        bottom: 16,
        left: spacing.base,
        right: spacing.base,
        zIndex: 15
    },
    pinCard: {
        backgroundColor: colors.surface,
        borderRadius: radius.xl,
        overflow: "hidden",
        borderWidth: 1.5,
        borderColor: colors.border,
        ...shadows.lg
    },
    cardTopAccent: {
        height: 3,
        width: "100%"
    },
    pinCardContent: {
        padding: spacing.lg,
        paddingTop: spacing.sm
    },
    pinCardCloseHandle: {
        alignItems: "center",
        paddingVertical: spacing.xs
    },
    pinCardHandle: {
        width: 36,
        height: 4,
        borderRadius: 2,
        backgroundColor: colors.gray300
    },
    // ── Cluster sheet (what a merged bubble opens) ──
    // Same footprint and chrome as the single-pin card, so a merged bubble
    // opening a list rather than a card doesn't read as a different surface.
    clusterSheetWrapper: {
        position: "absolute",
        bottom: 16,
        left: spacing.base,
        right: spacing.base,
        zIndex: 15
    },
    clusterSheet: {
        backgroundColor: colors.surface,
        borderRadius: radius.xl,
        overflow: "hidden",
        borderWidth: 1.5,
        borderColor: colors.border,
        ...shadows.lg
    },
    clusterSheetHeader: {
        paddingHorizontal: spacing.lg,
        paddingBottom: spacing.sm
    },
    clusterSheetTitle: {
        ...typography.subtitle,
        color: colors.textPrimary
    },
    clusterSheetSubtitle: {
        ...typography.caption,
        color: colors.textSecondary,
        marginTop: 2
    },
    // Capped so the map stays visible behind it: she is choosing between pins
    // she can still see, and a full-height sheet would hide the thing she is
    // choosing from. Four rows fit before it scrolls.
    clusterSheetList: {
        maxHeight: 264
    },
    clusterRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        paddingHorizontal: spacing.lg,
        paddingVertical: spacing.sm,
        borderTopWidth: 1,
        borderTopColor: colors.border
    },
    clusterRowAvatar: {
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: "center",
        justifyContent: "center"
    },
    clusterRowAvatarText: {
        fontSize: 12,
        fontFamily: fonts.extraBold,
        color: "#FFFFFF"
    },
    clusterRowBody: {
        flex: 1
    },
    clusterRowTitle: {
        ...typography.body,
        color: colors.textPrimary
    },
    clusterRowMeta: {
        ...typography.caption,
        color: colors.textSecondary
    },
    clusterRowRight: {
        alignItems: "flex-end",
        gap: 2
    },
    clusterRowPayment: {
        ...typography.subtitle,
        color: colors.textPrimary
    },
    clusterRowBadge: {
        borderRadius: radius.sm,
        paddingHorizontal: 6,
        paddingVertical: 1
    },
    clusterRowBadgeText: {
        fontSize: 11,
        fontFamily: fonts.semiBold
    },

    pinCardTopRow: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: spacing.md,
        marginBottom: spacing.md
    },
    pinCardAvatar: {
        width: 52,
        height: 52,
        borderRadius: 26,
        alignItems: "center",
        justifyContent: "center"
    },
    pinCardAvatarText: {
        fontSize: 18,
        fontFamily: fonts.extraBold,
        color: "#FFFFFF"
    },
    pinCardInfo: {
        flex: 1,
        gap: 3
    },
    pinCardHeadline: {
        flexDirection: "row",
        alignItems: "baseline",
        justifyContent: "space-between"
    },
    pinCardDateRange: {
        fontSize: 17,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    pinCardPatientName: {
        ...typography.subtitle,
        color: colors.textPrimary
    },
    pinCardAddress: {
        ...typography.caption,
        color: colors.textSecondary
    },
    pinCardAreaHint: {
        ...typography.caption,
        fontSize: 11,
        color: colors.textTertiary,
        marginTop: 2
    },
    pinCardPayment: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4
    },
    pinCardPaymentAmount: {
        fontSize: 22,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary
    },
    pinCardDollarIcon: {
        width: 24,
        height: 24,
        borderRadius: 12,
        borderWidth: 1.5,
        borderColor: colors.teal200,
        backgroundColor: colors.teal50,
        alignItems: "center",
        justifyContent: "center"
    },

    // Visit type + duration row
    pinCardTypeRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: spacing.md,
        paddingTop: spacing.md,
        borderTopWidth: 1,
        borderTopColor: colors.border
    },
    pinCardTypeLeft: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4
    },
    // Tinted chip pair (bg + text) from `exploreRelationStyle`, matching the
    // My Requests panel's badge treatment rather than the solid map-pin fill.
    pinCardRelationChip: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: radius.full
    },
    pinCardRelationText: {
        fontSize: 11,
        fontFamily: fonts.bold
    },
    pinCardTypeText: {
        ...typography.subtitle,
        color: colors.textPrimary
    },
    pinCardTypeSeparator: {
        ...typography.body,
        color: colors.textTertiary
    },
    pinCardDuration: {
        ...typography.subtitle,
        color: colors.textPrimary
    },
    // Relation badge — background is set inline from `exploreRelationStyle` so
    // the card chip and the map pin are always the same colour.
    relationBadge: {
        paddingHorizontal: 10,
        paddingVertical: 3,
        borderRadius: radius.full
    },
    relationBadgeText: {
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: "#FFFFFF"
    },
    cardUnseenDot: {
        position: "absolute",
        top: -2,
        right: -2,
        width: 12,
        height: 12,
        borderRadius: 6,
        backgroundColor: exploreUnseenDot,
        borderWidth: 2,
        borderColor: colors.surface
    },

    // Travel time row
    travelRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.md,
        marginBottom: spacing.lg,
        paddingVertical: spacing.md,
        backgroundColor: colors.gray50,
        borderRadius: radius.md
    },
    travelItem: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4
    },
    travelText: {
        ...typography.caption,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    travelDot: {
        width: 4,
        height: 4,
        borderRadius: 2,
        backgroundColor: colors.gray300
    },

    // CTA button
    ctaButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm,
        paddingVertical: 14,
        borderRadius: radius.md,
        ...shadows.brand
    },
    ctaText: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    },
    ctaArrowCircle: {
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: "rgba(255,255,255,0.25)",
        alignItems: "center",
        justifyContent: "center"
    },

    // ── Cards View ──
    cardsContainer: {
        flex: 1,
        backgroundColor: colors.background
    },
    cardsList: {
        paddingHorizontal: spacing.base,
        paddingBottom: 120
    },

    // ── Visit Card ──
    visitCard: {
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        overflow: "hidden",
        borderWidth: 1.5,
        borderColor: colors.border,
        ...shadows.sm
    },
    cardContent: {
        flexDirection: "row",
        alignItems: "center",
        padding: spacing.base,
        gap: spacing.md
    },
    cardAvatar: {
        width: 44,
        height: 44,
        borderRadius: 22,
        alignItems: "center",
        justifyContent: "center"
    },
    cardAvatarText: {
        fontSize: 14,
        fontFamily: fonts.extraBold,
        color: "#FFFFFF"
    },
    cardBody: {
        flex: 1,
        gap: 8
    },
    cardPatientName: {
        ...typography.subtitle,
        color: colors.textPrimary
    },
    cardTopLine: {
        flexDirection: "row",
        alignItems: "baseline",
        justifyContent: "space-between"
    },
    cardDateRange: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    cardPayment: {
        fontSize: 20,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary
    },
    chipRow: {
        flexDirection: "row",
        gap: 6,
        flexWrap: "wrap"
    },
    chip: {
        flexDirection: "row",
        alignItems: "center",
        gap: 3,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: radius.full
    },
    chipText: {
        ...typography.micro,
        letterSpacing: 0.3
    },
    cardBottomLine: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: spacing.sm
    },
    cardAddress: {
        ...typography.caption,
        color: colors.textTertiary,
        flex: 1
    },

    // ── Resubmission Banner ──
    resubmissionBanner: {
        marginTop: spacing.sm,
        padding: spacing.sm,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: colors.teal200,
        backgroundColor: colors.teal50
    },
    resubmissionBannerHeader: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs,
        marginBottom: 4
    },
    resubmissionBannerTitle: {
        flex: 1,
        fontFamily: fonts.bold,
        fontSize: 13,
        color: colors.teal600
    },
    resubmissionVisitRow: {
        minHeight: 32,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: spacing.xs
    },
    resubmissionVisitName: {
        flex: 1,
        fontFamily: fonts.semiBold,
        fontSize: 12,
        color: colors.textPrimary
    },

    // ── FABs ──
    fabColumn: {
        position: "absolute",
        bottom: 24,
        right: spacing.base,
        gap: spacing.md,
        zIndex: 15,
        alignItems: "center"
    },
    fabPrimary: {
        width: 56,
        height: 56,
        borderRadius: 28,
        alignItems: "center",
        justifyContent: "center",
        ...shadows.brand
    }
});
